import React from 'react';
import { Form, Formik, FormikConfig } from 'formik';
import * as yup from 'yup';
import { IconChevronDown, IconChevronUp } from '@maiia/design-system';
import Skeleton from '@mui/material/Skeleton';
import {
  Button,
  Stack,
  Alert,
  Typography,
  DialogContent,
  DialogActions,
  Link,
} from '@mui/material';
import { CardHcd } from '@maiia/model/generated/model/api-patient/api-patient';
import { Trans, useTranslation } from '@/src/i18n';

import PractitionerItem from '../../molecules/PractitionerItem/PractitionerItem';
import useCareTeam from '@/src/hooks/useCareTeam';
import { splitCareTeam } from '@/components/utils';

import Text from '@/components/atoms/Text/Text';
import ShareThirdPartyAction from './ShareThirdPartyAction';
import Dialog from '@/components/molecules/Dialog/Dialog';
import useBranding from '@/src/hooks/branding/useBranding';
import { isMutual } from '@/components/utils/teleconsultationRelay';

export type HandleChooseItemsListFunc = (arg0: {
  sharedThirdParty: boolean;
  cards: CardHcd[];
}) => void;

type PractitionerChooserDialogProps = {
  isOpen: boolean;
  handleClose: React.ReactEventHandler;
  handleChooseItemsList: HandleChooseItemsListFunc;
  profileId?: string;
  documentId: string;
};

type PractitionerChooserDialogBodyProps = {
  profileId: PractitionerChooserDialogProps['profileId'];
  documentId: string;
};
type FormValues = {
  sharedThirdParty: boolean;
  cards: string[];
};

// The max number of practitioners to show before adding a " show more"  button
const ITEMS_LIMIT = 3;

const schema = yup.object({
  cards: yup.array().when('sharedThirdParty', {
    is: true,
    then: (childSchema: yup.ArraySchema<string>) => childSchema.min(0),
    otherwise: (childSchema: yup.ArraySchema<string>) => childSchema.min(1),
  }),
  sharedThirdParty: yup.boolean(),
});

export const PractitionerChooserDialogBody = ({
  profileId,
  documentId,
}: PractitionerChooserDialogBodyProps) => {
  const { isLoading, data } = useCareTeam();
  const { t } = useTranslation();
  const [showMore, toggleShowMore] = React.useState(false);
  const { data: fullPharmacyInfo } = useBranding();

  // We don't do special actions when the tlcRelay is a mutual
  const hasThirdParty = fullPharmacyInfo?.code && !isMutual(fullPharmacyInfo);

  if (isLoading)
    return (
      <Stack spacing={2}>
        <Skeleton height={30} />
        <Skeleton height={71} width="100%" />
        <Skeleton height={71} width="100%" />
        <Skeleton height={71} width="100%" />
        <Skeleton height={71} width="100%" />
        <Skeleton height={71} width="100%" />
      </Stack>
    );
  if (!data) return null;

  const { entities, pharmacies, hasEntity, hasPharmacies } = splitCareTeam(
    data.filter(entity => {
      const isProfileFavorite = entity.profiles?.some(
        profile => profile.id === profileId,
      );
      return isProfileFavorite;
    }),
  );

  if (!hasEntity && !hasPharmacies && !hasThirdParty) {
    // Help "Add a favorite" url
    const url =
      'https://maiia-guide.zendesk.com/hc/fr/articles/360018800099-Ajouter-un-m%C3%A9decin-en-favori-ou-en-m%C3%A9decin-traitant';

    return (
      <Text
        component="p"
        variant="paragraph"
        style={{ whiteSpace: 'break-spaces' }}
      >
        <Trans
          i18nKey="add_practitioner_or_pharmacy_to_favorite_first"
          components={{
            a: (
              <Link
                href={url}
                title={t('how_to_add_practitioner_or_pharmacy_to_favorite')}
                aria-label={t(
                  'how_to_add_practitioner_or_pharmacy_to_favorite',
                )}
                target="_blank"
              />
            ),
          }}
        />
      </Text>
    );
  }
  const itemsToShow = showMore ? entities.length : ITEMS_LIMIT;

  return (
    <Stack spacing={2}>
      {hasEntity && (
        <>
          <Typography sx={{ mb: 0 }} color="primary" variant="h6">
            {t('share-to-practitioner')}
          </Typography>
          <div>
            {entities.slice(0, itemsToShow).map(({ card }) => (
              <PractitionerItem
                key={`${card?.professional?.practitionerId}_${card?.center?.id}`}
                card={card}
                documentId={documentId}
                withCheckbox
                CheckboxProps={{ name: 'cards' }}
              />
            ))}
          </div>
          {entities.length > ITEMS_LIMIT && (
            <Button
              sx={{ mt: 3 }}
              color="info"
              variant="text"
              endIcon={!showMore ? <IconChevronDown /> : <IconChevronUp />}
              onClick={() => toggleShowMore(prevState => !prevState)}
            >
              {!showMore ? t('show_more') : t('show_less')}
            </Button>
          )}
          <Alert
            severity="warning"
            sx={theme => ({
              maxWidth: 'fit-content',
              [theme.breakpoints.up('sm')]: {
                minWidth: 435,
                maxWidth: '35vw',
                alignSelf: 'center',
              },
            })}
          >
            {t('share-document-warning')}
          </Alert>
        </>
      )}
      {(hasPharmacies || hasThirdParty) && (
        <div>
          <Typography color="primary" variant="h6">
            {t('share-to-pharmacie')}
          </Typography>

          {hasThirdParty && (
            <ShareThirdPartyAction fullPharmacyInfo={fullPharmacyInfo} />
          )}

          {pharmacies.map(({ card }) => (
            <PractitionerItem
              key={card?.center?.id}
              card={card}
              documentId={documentId}
              withCheckbox
              CheckboxProps={{ name: 'cards' }}
            />
          ))}
        </div>
      )}
    </Stack>
  );
};

const PractitionerChooserDialog = ({
  isOpen,
  handleClose,
  handleChooseItemsList,
  profileId,
  documentId,
}: PractitionerChooserDialogProps) => {
  const { t } = useTranslation();
  const { data } = useCareTeam();
  const cardsById = React.useMemo(
    () =>
      (data ?? []).reduce((prev, curr) => {
        if (typeof curr.card?.id === 'undefined') {
          return prev;
        }
        // eslint-disable-next-line no-param-reassign
        prev[curr.card.id] = curr.card;
        return prev;
      }, {}),
    [data],
  );

  const onSubmit = React.useCallback<FormikConfig<FormValues>['onSubmit']>(
    values => {
      const newValues = {
        sharedThirdParty: values.sharedThirdParty,
        cards: values.cards.map(id => cardsById[id]),
      };
      handleChooseItemsList(newValues);
    },
    [handleChooseItemsList, cardsById],
  );
  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="simple-dialog-title"
      open={isOpen}
      title={t('choose_document_share')}
      disableRestoreFocus
      scroll="paper"
    >
      <Formik<FormValues>
        onSubmit={onSubmit}
        initialValues={{
          cards: [],
          sharedThirdParty: false,
        }}
        validationSchema={schema}
        validateOnMount
      >
        {({ isValid }) => (
          <Form
            // This style to fix issue => (wrapping dialogActions with formik) to make buttons fixed instead scroll
            style={{
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <DialogContent sx={{ border: 'none' }}>
              <PractitionerChooserDialogBody
                profileId={profileId}
                documentId={documentId}
              />
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'end' }}>
              <Stack direction="row" spacing={2}>
                <Button variant="text" color="secondary" onClick={handleClose}>
                  {t('action_cancel')}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={!isValid}
                  data-testid="validate-button"
                >
                  {t('action_validate')}
                </Button>
              </Stack>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
export default PractitionerChooserDialog;
